import type { ContactVersion } from '@common/types/contacts';
import { formLockedByClientMessage } from '@common/types/formLocks';
import type { ProspectResponse } from '@common/types/prospects';
import { getMatterTypeLabel } from '@common/util';
import { getActiveContactEmailBounce } from '@common/utils/contactEmailBounce';
import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import AddIcon from '@mui/icons-material/Add';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Link as MuiLink,
    Select,
    type SelectChangeEvent,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import LexoSwitch from '@/components/LexoSwitch';
import EditableViewComponent from '@/components/ReadOnlyComponent/EditableViewComponent';
import { prospectFormLockQueryKey, useProspectFormLockStatus } from '@/queries/formLocks';
import { prospectDetailsQueryKey } from '@/queries/prospectDetails';
import { useUsers } from '@/queries/users';
import {
    addIntakeContact,
    removeIntakeContact,
    updateContactById,
} from '@/services/ContactService';
import { patchProspectById, updateProspectAssignment } from '@/services/ProspectService';
import { formatCurrency } from '@/utils';
import { errorSnackbar, successSnackbar, warningSnackbar } from '@/utils/snackbar';
import { applyProspectFieldUpdate, mergePatchResponse, toPatchPath } from '../prospectDetailsState';
import ResendIntakeDialog from '../ResendIntakeDialog';
import ResendPostIntakeDialog from '../ResendPostIntakeDialog';
import AddContactModal, { type NewContactFields } from './AddContactModal';
import ContactEmailBounceAlert from './ContactEmailBounceAlert';
import ContactEmailBounceChip from './ContactEmailBounceChip';
import EditContactModal from './EditContactModal';
import RemoveContactDialog from './RemoveContactDialog';

interface Props {
    prospectId?: string;
    prospect: ProspectResponse;
    selectedVersionId: number | null;
}

export default function ProspectSummaryIntakeOnly({
    prospectId,
    prospect,
    selectedVersionId,
}: Props) {
    const { prospectSummary } = prospect;
    const queryClient = useQueryClient();

    const updateProspectInCache = (updater: (prev: ProspectResponse) => ProspectResponse) => {
        queryClient.setQueryData(
            prospectDetailsQueryKey(prospectId, selectedVersionId),
            (prev: ProspectResponse | undefined) => (prev ? updater(prev) : prev),
        );
    };

    const updateProspectField = (name: string, value: unknown) => {
        updateProspectInCache((prev) => applyProspectFieldUpdate(prev, name, value));
    };

    const handleValueChange = async (name: string, value: unknown) => {
        updateProspectField(name, value);
        try {
            const response = await patchProspectById(prospectId as string, [
                {
                    op: 'replace',
                    path: toPatchPath(name),
                    value: value ?? null,
                },
            ]);
            queryClient.setQueryData(
                prospectDetailsQueryKey(prospectId, selectedVersionId),
                (prev: ProspectResponse | undefined) =>
                    prev ? mergePatchResponse(prev, response, name) : response,
            );
        } catch (error) {
            console.error('API request error:', error);
            warningSnackbar('Please refresh the page!');
            await queryClient.invalidateQueries({
                queryKey: ['prospect', prospectId],
            });
        }
    };
    const theme = useTheme();
    const assignedLawyerSelectId = useId();
    const { data: users = [] } = useUsers({ lawFirmId: prospect.lawFirmId });
    const [editingContact, setEditingContact] = useState<ContactVersion | null>(null);
    const [contactToRemove, setContactToRemove] = useState<ContactVersion | null>(null);
    const [isAddContactOpen, setIsAddContactOpen] = useState(false);
    const [intakeEmailPrompt, setIntakeEmailPrompt] = useState<{
        contact: ContactVersion;
        mode: 'send' | 'resend';
    } | null>(null);
    const [contactToResendPostIntake, setContactToResendPostIntake] =
        useState<ContactVersion | null>(null);
    const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);
    const assignedLawyerId = prospect.quoteApproveId ?? null;
    const ccPerson = prospect.prospectOverride?.intakeCCEmails ?? [];
    const latestProspectVersionId = prospect.prospectVersions?.[0]?.prospectVersionId ?? null;
    const isViewingLatestVersion =
        selectedVersionId === null || selectedVersionId === latestProspectVersionId;
    const canEditContacts =
        latestProspectVersionId !== null &&
        isViewingLatestVersion &&
        (prospect.prospectStatus === 'IntakeSent' || prospect.prospectStatus === 'IntakeCompleted');
    const canEditAssignment = isViewingLatestVersion;
    const { data: formLockStatus } = useProspectFormLockStatus(prospectId, canEditContacts);
    const isFormLocked = !!formLockStatus?.isLocked;
    const canAddContact = canEditContacts;
    const canRemoveContact = canAddContact && prospect.multipleContacts.length > 1;
    const isIntakeFormV2 = getIsIntakeFormV2(prospect.practiceArea, prospect.matterType);

    const availableCcContacts = users
        .filter((user) => user.userId !== assignedLawyerId)
        .map((user) => user.email);

    const saveAssignment = async (quoteApproveId: number, intakeCCEmails: string[]) => {
        if (!prospectId) return;

        setIsAssignmentSaving(true);
        try {
            const response = await updateProspectAssignment(prospectId, {
                quoteApproveId,
                intakeCCEmails,
            });
            queryClient.setQueryData(
                prospectDetailsQueryKey(prospectId, selectedVersionId),
                response,
            );
            successSnackbar('Email assignment updated successfully.');
        } catch (error) {
            console.error('Error updating email assignment:', error);
            errorSnackbar('Error updating email assignment');
            await queryClient.invalidateQueries({
                queryKey: prospectDetailsQueryKey(prospectId, selectedVersionId),
            });
        } finally {
            setIsAssignmentSaving(false);
        }
    };

    const handleAssignedLawyerChange = async (value: number | null) => {
        if (!value || value === assignedLawyerId || !canEditAssignment) return;

        const assignedLawyerEmail = users.find((user) => user.userId === value)?.email;
        const nextCcPerson = assignedLawyerEmail
            ? ccPerson.filter((email) => email !== assignedLawyerEmail)
            : ccPerson;

        await saveAssignment(value, nextCcPerson);
    };

    const handleCcPersonChange = async (value: string[]) => {
        if (!assignedLawyerId || !canEditAssignment) return;

        const assignedLawyerEmail = users.find((user) => user.userId === assignedLawyerId)?.email;
        const nextCcPerson = assignedLawyerEmail
            ? value.filter((email) => email !== assignedLawyerEmail)
            : value;

        if (
            nextCcPerson.length === ccPerson.length &&
            nextCcPerson.every((email, index) => email === ccPerson[index])
        ) {
            return;
        }

        await saveAssignment(assignedLawyerId, nextCcPerson);
    };

    let pricingLabel = '';
    if (prospect.matterType === 'Sale') {
        pricingLabel = 'Sale price';
    } else if (prospect.matterType === 'Purchase') {
        pricingLabel = 'Purchase price';
    } else if (prospect.matterType === 'Refinance') {
        pricingLabel = 'Total loan amount';
    }

    const purchasePrice = prospectSummary.purchasePrice;
    const pricingValue = purchasePrice == null ? '-' : `$${formatCurrency(purchasePrice)}`;

    const handleReminderChange = async (reminderType: string, value: boolean) => {
        try {
            handleValueChange('isIntakeReminderEnabled', value);
        } catch (error) {
            console.error(`Error updating ${reminderType} email reminder:`, error);
            errorSnackbar(`Error updating ${reminderType} email reminder`);
        }
    };

    const handleChange = async (value: string) => {
        if ((!value && prospect.customFileNumber === '') || value === prospect.customFileNumber)
            return;

        if (value && value?.length > 400) {
            errorSnackbar('Custom file number must be less than 400 characters.');
            return;
        }

        try {
            const response = await patchProspectById(prospectId as string, [
                {
                    op: 'replace',
                    path: '/customFileNumber',
                    value: value,
                },
            ]);
            queryClient.setQueryData(
                prospectDetailsQueryKey(prospectId, selectedVersionId),
                response,
            );
            successSnackbar('Custom file number updated successfully!');
        } catch (error) {
            console.error('API error:', error);
        }
    };

    const handleContactMutationError = async (error: unknown, fallback: string) => {
        console.error(fallback, error);
        const response = (error as { response?: { status?: number; data?: string } })?.response;
        errorSnackbar(response?.data || fallback);
        if (response?.status === 409) {
            await queryClient.invalidateQueries({
                queryKey: prospectFormLockQueryKey(prospectId),
            });
        }
    };

    const handleAddContact = async (contact: NewContactFields) => {
        if (!canAddContact || !prospectId) {
            errorSnackbar('You cannot add a contact for this prospect.');
            return;
        }

        try {
            const newContact = await addIntakeContact({
                prospectId,
                firstName: contact.firstName,
                middleName: contact.middleName || undefined,
                lastName: contact.lastName,
                contactEmail: contact.contactEmail,
                phoneNumber: contact.phoneNumber || undefined,
            });

            updateProspectInCache((prev) =>
                applyProspectFieldUpdate(prev, 'multipleContacts', [
                    ...prev.multipleContacts,
                    newContact,
                ]),
            );
            setIsAddContactOpen(false);
            successSnackbar('Contact added successfully.');

            if (prospect.prospectStatus === 'IntakeSent') {
                setIntakeEmailPrompt({ contact: newContact, mode: 'send' });
            } else if (prospect.prospectStatus === 'IntakeCompleted') {
                setContactToResendPostIntake(newContact);
            }
        } catch (error: unknown) {
            await handleContactMutationError(error, 'Error adding contact');
        }
    };

    const handleSaveContact = async (contact: ContactVersion) => {
        if (!canEditContacts) {
            errorSnackbar('Contacts can only be updated on the latest prospect version.');
            return;
        }

        try {
            const updatedContact = await updateContactById(contact.contactId, {
                firstName: contact.firstName,
                middleName: contact.middleName ?? '',
                lastName: contact.lastName,
                email: contact.contactEmail,
                phoneNumber: contact.phoneNumber ?? '',
            });

            updateProspectInCache((prev) => {
                let next = applyProspectFieldUpdate(
                    prev,
                    'multipleContacts',
                    prev.multipleContacts.map((existingContact) =>
                        existingContact.contactId === updatedContact.contactId
                            ? updatedContact
                            : existingContact,
                    ),
                );
                if (prev.contact?.contactId === updatedContact.contactId) {
                    next = applyProspectFieldUpdate(next, 'contact', updatedContact);
                }
                return next;
            });

            setEditingContact(null);
            successSnackbar('Contact updated successfully!');

            if (prospect.prospectStatus === 'IntakeSent') {
                setIntakeEmailPrompt({ contact: updatedContact, mode: 'resend' });
            } else if (prospect.prospectStatus === 'IntakeCompleted') {
                setContactToResendPostIntake(updatedContact);
            }
        } catch (error: unknown) {
            await handleContactMutationError(error, 'Error updating contact');
        }
    };

    const handleRemoveContact = async (contact: ContactVersion) => {
        if (!canRemoveContact || !prospectId) {
            errorSnackbar('You cannot remove a contact for this prospect.');
            return;
        }

        try {
            const { remainingContacts, primaryContact } = await removeIntakeContact({
                prospectId,
                contactId: contact.contactId,
            });

            updateProspectInCache((prev) => {
                let next = applyProspectFieldUpdate(prev, 'multipleContacts', remainingContacts);
                next = applyProspectFieldUpdate(next, 'contact', primaryContact);
                next = applyProspectFieldUpdate(next, 'contactId', primaryContact.contactId);
                return next;
            });
            setContactToRemove(null);
            setEditingContact(null);
            successSnackbar('Contact removed successfully.');

            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['prospect-intake-form', prospectId] }),
                queryClient.invalidateQueries({
                    queryKey: ['prospect-client-documents', prospectId],
                }),
            ]);
        } catch (error: unknown) {
            await handleContactMutationError(error, 'Error removing contact');
        }
    };

    let contactPartyLabel = 'Contact';

    if (prospect.matterType === 'PrivateLender') {
        contactPartyLabel = 'Lender';
    } else if (prospect.matterType === 'Purchase') {
        contactPartyLabel = 'Purchaser';
    } else if (prospect.matterType === 'Sale') {
        contactPartyLabel = 'Seller';
    } else if (prospect.matterType === 'Refinance') {
        contactPartyLabel = 'Borrower';
    }

    const displayHeader =
        contactPartyLabel === 'Contact' ? 'Contact info' : `${contactPartyLabel}(s) info`;
    const addContactLabel = `Add ${contactPartyLabel}`;

    const contacts: ContactVersion[] =
        prospect.multipleContacts.length > 0
            ? prospect.multipleContacts
            : prospect.contact
              ? [prospect.contact]
              : [];

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '24px',
                height: 'fit-content',
                width: 'calc(100% - 48px)',
                padding: '24px',
            }}
        >
            <Box
                sx={{
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.neutral.borderColor}`,
                    backgroundColor: 'neutral.white',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '18px',
                    height: 'fit-content',
                    boxSizing: 'border-box',
                    width: '100%',
                    maxWidth: '860px',
                }}
            >
                <Typography variant="h3">Prospect information</Typography>
                {prospect?.createdAs === 'Manually' && (
                    <Box width="100%" display="flex" gap="10px" alignItems="center">
                        <LexoSwitch
                            name="intakeEmailReminder"
                            checked={prospect.isIntakeReminderEnabled}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                handleReminderChange('intake', event.target.checked)
                            }
                            disabled={prospect.prospectStatus === 'IntakeCompleted'}
                        />
                        <Typography variant="body1">Email Reminder for Intake</Typography>
                    </Box>
                )}
                <EditableViewComponent
                    label="Custom file number"
                    value={prospect.customFileNumber}
                    isEditable={true}
                    onChange={(value) => {
                        handleChange(value);
                    }}
                    maxLength={400}
                    errorMessage="Custom file number must be less than 400 characters."
                />
                <EditableViewComponent label="Prospect name" value={prospect.prospectName} />
                <EditableViewComponent
                    label="Matter type"
                    value={getMatterTypeLabel(prospect.matterType)}
                />
                {prospect.quoteTemplate?.quoteTemplateName && (
                    <EditableViewComponent
                        label="Intake template"
                        value={prospect.quoteTemplate.quoteTemplateName}
                        link={`/intake-only/setup/matter-type/${prospect.quoteTemplateId}`}
                    />
                )}
                {prospect.practiceArea === 'RealEstate' && !isIntakeFormV2 && pricingLabel && (
                    <EditableViewComponent label={pricingLabel} value={pricingValue} />
                )}
                <Typography variant="h3">Email assignment</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <FormControl
                        required
                        sx={{ width: { xs: '100%', md: 'calc(40% - 6px)' } }}
                        disabled={!canEditAssignment || isAssignmentSaving}
                    >
                        <InputLabel id={assignedLawyerSelectId}>Assigned user</InputLabel>
                        <Select<number>
                            labelId={assignedLawyerSelectId}
                            label="Assigned user"
                            value={assignedLawyerId ?? ''}
                            sx={{ backgroundColor: 'white' }}
                            onChange={(event: SelectChangeEvent<number>) => {
                                const value =
                                    event.target.value === '' ? null : Number(event.target.value);
                                void handleAssignedLawyerChange(value);
                            }}
                        >
                            {users.map(({ userId, fullName }) => (
                                <MenuItem key={userId} value={userId}>
                                    {fullName}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            Emails will be sent on behalf of this lawyer or clerk
                        </FormHelperText>
                    </FormControl>
                    <FormControl
                        sx={{ width: { xs: '100%', md: 'calc(60% - 6px)' } }}
                        disabled={!canEditAssignment || isAssignmentSaving || !assignedLawyerId}
                    >
                        <AutoCompleteMultiSelect
                            fullWidth
                            options={availableCcContacts}
                            getOptionLabel={(option) => option}
                            label="CC list"
                            value={ccPerson}
                            onChange={(_event, value) => {
                                void handleCcPersonChange(value);
                            }}
                            sx={{ backgroundColor: 'white' }}
                        />
                        <FormHelperText>
                            Additional lawyers or clerks to be CC'd in client emails (opt out of
                            notifications in{' '}
                            <MuiLink component={RouterLink} to="/settings/notification">
                                Settings
                            </MuiLink>
                            )
                        </FormHelperText>
                    </FormControl>
                </Stack>
                {isFormLocked && formLockStatus?.lockedByEmail && (
                    <Typography variant="body2" color="text.secondary">
                        {formLockedByClientMessage(formLockStatus.lockedByEmail)}
                    </Typography>
                )}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%' }}
                >
                    <Typography variant="h3">{displayHeader}</Typography>
                    {canAddContact && (
                        <Button
                            variant="outlined"
                            size="small"
                            aria-label={addContactLabel}
                            startIcon={<AddIcon fontSize="small" />}
                            disabled={isFormLocked}
                            onClick={() => setIsAddContactOpen(true)}
                        >
                            {addContactLabel}
                        </Button>
                    )}
                </Stack>
                {contacts.map((contact) => (
                    <Accordion
                        key={contact.contactId}
                        defaultExpanded
                        disableGutters
                        elevation={0}
                        sx={{
                            width: '100%',
                            border: `1px solid ${theme.palette.neutral.N_300}`,
                            borderRadius: '4px !important',
                            boxShadow: 'none',
                            '&::before': {
                                content: 'none',
                            },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ width: '100%', pr: 1 }}
                            >
                                <Stack sx={{ minWidth: 0 }}>
                                    <Typography variant="h4">
                                        {contact.contactName || 'NA'}
                                    </Typography>
                                    {contact.contactEmail && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2">
                                                {contact.contactEmail}
                                            </Typography>
                                            <ContactEmailBounceChip contact={contact} />
                                        </Stack>
                                    )}
                                </Stack>
                                {canEditContacts && (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            aria-label="Edit contact"
                                            startIcon={<EditNoteIcon fontSize="small" />}
                                            disabled={isFormLocked}
                                            onClick={() => setEditingContact(contact)}
                                        >
                                            Edit
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ContactEmailBounceAlert
                                contact={contact}
                                sx={{ marginBottom: '10px' }}
                            />
                            <EditableViewComponent
                                label="Client"
                                value={contact.contactName || 'NA'}
                            />
                            <Box sx={{ marginTop: '10px' }}>
                                <EditableViewComponent label="Email" value={contact.contactEmail} />
                            </Box>
                            <Box sx={{ marginTop: '10px' }}>
                                <EditableViewComponent
                                    label="Cell number"
                                    value={contact.phoneNumber}
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
            {isAddContactOpen && (
                <AddContactModal
                    onClose={() => setIsAddContactOpen(false)}
                    onSave={handleAddContact}
                />
            )}
            {editingContact && (
                <EditContactModal
                    contact={editingContact}
                    onClose={() => setEditingContact(null)}
                    onSave={handleSaveContact}
                    onRemove={
                        canRemoveContact ? () => setContactToRemove(editingContact) : undefined
                    }
                />
            )}
            {contactToRemove && (
                <RemoveContactDialog
                    contactName={contactToRemove.contactName}
                    contactEmail={contactToRemove.contactEmail}
                    onClose={() => setContactToRemove(null)}
                    onConfirm={() => handleRemoveContact(contactToRemove)}
                />
            )}
            {intakeEmailPrompt && prospectId && (
                <ResendIntakeDialog
                    prospectId={prospectId}
                    contactId={intakeEmailPrompt.contact.contactId}
                    contactName={intakeEmailPrompt.contact.contactName}
                    bouncedEmail={
                        getActiveContactEmailBounce(intakeEmailPrompt.contact)
                            ? intakeEmailPrompt.contact.contactEmail
                            : null
                    }
                    mode={intakeEmailPrompt.mode}
                    onClose={() => setIntakeEmailPrompt(null)}
                />
            )}
            {contactToResendPostIntake && prospectId && (
                <ResendPostIntakeDialog
                    prospectId={prospectId}
                    contactId={contactToResendPostIntake.contactId}
                    contactName={contactToResendPostIntake.contactName}
                    onClose={() => setContactToResendPostIntake(null)}
                />
            )}
        </Box>
    );
}
